import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { useToast } from '../hooks/use-toast';
import { Plus, Car as CarIcon, Calendar, DollarSign, Clock } from 'lucide-react';
import MyCars from '../components/MyCars';
import StripeConnectBanner from '../components/StripeConnectBanner';
import MobilitierReferralsPanel from '../components/MobilitierReferralsPanel';
import ActivateDualRoleBanner from '../components/ActivateDualRoleBanner';

const MobilitierDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [bookings, setBookings] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchData = async () => {
    try {
      const { bookingsAPI, carsAPI } = await import('../api');
      const [bookingsRes, carsRes] = await Promise.all([
        bookingsAPI.getAll(),
        carsAPI.getMine(),
      ]);
      setBookings(bookingsRes.data || []);
      setCars(carsRes.data || []);
    } catch (e) {
      console.error('Error fetching data:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      const { bookingsAPI } = await import('../api');
      await bookingsAPI.approve(id);
      toast({ title: 'Reserva aprobada', description: 'El usuario fue notificado' });
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo aprobar', variant: 'destructive' });
    }
  };

  const handleReject = async (id) => {
    try {
      const { bookingsAPI } = await import('../api');
      await bookingsAPI.reject(id, 'No disponible');
      toast({ title: 'Reserva rechazada', description: 'El usuario fue notificado' });
      fetchData();
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo rechazar', variant: 'destructive' });
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><LoadingSpinner size="lg" /></div>;

  const pending = bookings.filter(b => b.status === 'pending');
  const earnings = bookings
    .filter(b => b.status === 'completed')
    .reduce((s, b) => s + (b.owner_earnings || 0), 0);
  const activeCars = cars.filter(c => c.available).length;

  return (
    <div className="min-h-screen bg-ink-50 py-6 sm:py-10 font-mont" data-testid="mobilitier-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-3">
          <div>
            <h1 className="font-comfortaa font-bold text-2xl sm:text-3xl text-ink">Dashboard Mobilitier</h1>
            <p className="text-sm text-ink-500 font-light mt-1">Hola {user?.name?.split(' ')[0]}, gestiona tus autos y reservas.</p>
          </div>
          <Button
            onClick={() => navigate('/mobilitier/add-car')}
            className="rounded-full h-11 px-5 bg-lime hover:bg-lime-500 text-ink font-comfortaa font-bold w-full sm:w-auto"
            data-testid="add-car-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Agregar Auto
          </Button>
        </div>

        <StripeConnectBanner />

        <ActivateDualRoleBanner target="user" />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <Card data-testid="stat-cars" className="border-ink-100">
            <CardContent className="p-4 sm:p-5">
              <CarIcon className="w-5 h-5 text-ink-700 mb-2" />
              <p className="font-comfortaa font-bold text-2xl sm:text-3xl text-ink">{cars.length}</p>
              <p className="text-xs text-ink-500 font-light mt-1">Autos ({activeCars} activos)</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-pending" className="border-ink-100">
            <CardContent className="p-4 sm:p-5">
              <Clock className="w-5 h-5 text-amber-600 mb-2" />
              <p className="font-comfortaa font-bold text-2xl sm:text-3xl text-ink">{pending.length}</p>
              <p className="text-xs text-ink-500 font-light mt-1">Pendientes</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-bookings" className="border-ink-100">
            <CardContent className="p-4 sm:p-5">
              <Calendar className="w-5 h-5 text-ink-700 mb-2" />
              <p className="font-comfortaa font-bold text-2xl sm:text-3xl text-ink">{bookings.length}</p>
              <p className="text-xs text-ink-500 font-light mt-1">Reservas totales</p>
            </CardContent>
          </Card>
          <Card data-testid="stat-earnings" className="border-ink-100 bg-ink text-white">
            <CardContent className="p-4 sm:p-5">
              <DollarSign className="w-5 h-5 text-lime mb-2" />
              <p className="font-comfortaa font-bold text-2xl sm:text-3xl">${Math.round(earnings).toLocaleString()}</p>
              <p className="text-xs text-white/60 font-light mt-1">Ganancias MXN</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="cars" className="space-y-4">
          <TabsList>
            <TabsTrigger value="cars" data-testid="tab-cars">
              Mis Autos ({cars.length})
            </TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">
              Reservas {pending.length > 0 && <Badge className="ml-2 bg-orange-500">{pending.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="referrals" data-testid="tab-referrals">
              Referidos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="cars">
            <MyCars />
          </TabsContent>

          <TabsContent value="bookings" className="space-y-4">
            <Card>
              <CardHeader><CardTitle>Aprobaciones Pendientes</CardTitle></CardHeader>
              <CardContent>
                {pending.length > 0 ? (
                  <div className="space-y-3">
                    {pending.map(b => (
                      <div key={b.id} className="border rounded-lg p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2" data-testid={`pending-booking-${b.id}`}>
                        <div className="flex-1">
                          <h4 className="font-semibold cursor-pointer hover:text-blue-600" onClick={() => navigate(`/booking-details/${b.id}`)}>
                            {b.car_name || 'Auto'} · #{b.id.slice(-6)}
                          </h4>
                          <p className="text-xs text-gray-600 mt-0.5">
                            {new Date(b.start_date).toLocaleDateString()} → {new Date(b.end_date).toLocaleDateString()}
                          </p>
                          <p className="text-xs text-gray-600">Renter: {b.user_name || '—'} · Total ${b.total_price} · Ganas ${b.owner_earnings}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleApprove(b.id)} data-testid={`approve-btn-${b.id}`}>Aprobar</Button>
                          <Button size="sm" variant="destructive" onClick={() => handleReject(b.id)} data-testid={`reject-btn-${b.id}`}>Rechazar</Button>
                          <Button size="sm" variant="outline" onClick={() => navigate(`/booking-details/${b.id}`)}>Ver</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600 text-center py-6 text-sm">No hay reservas pendientes</p>
                )}
              </CardContent>
            </Card>

            {bookings.filter(b => b.status !== 'pending').length > 0 && (
              <Card>
                <CardHeader><CardTitle>Historial</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {bookings.filter(b => b.status !== 'pending').map(b => (
                      <div key={b.id} className="border-b pb-2 last:border-0 flex justify-between items-center">
                        <div>
                          <p className="font-medium text-sm">{b.car_name || 'Auto'} · #{b.id.slice(-6)}</p>
                          <p className="text-xs text-gray-600">
                            {new Date(b.start_date).toLocaleDateString()} → {new Date(b.end_date).toLocaleDateString()} · ${b.total_price}
                          </p>
                        </div>
                        <Badge variant="outline">{b.status}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="referrals">
            <MobilitierReferralsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MobilitierDashboard;
